import { AxiosResponse } from 'axios';

import { SECURE_API } from 'src/api/api';
import { ListCrudConfig } from 'src/stores/config/listCrud';
import { runApiAction } from 'src/stores/lib/runApiAction';
import { StoreGetter, StoreSetter } from 'src/stores/lib/types';

type ListDispatchAction<TItem> = { type: 'setList'; items: TItem[] } | { type: 'reset' };

class ListCrudActionImpl<
  TStore extends Record<string, unknown>,
  TItem extends { id: string },
  TFetchOptions = void,
  TSavePayload = TItem,
> {
  readonly #set: StoreSetter<TStore>;
  readonly #config: ListCrudConfig<TItem, TFetchOptions>;

  constructor(
    set: StoreSetter<TStore>,
    _get: StoreGetter<TStore>,
    config: ListCrudConfig<TItem, TFetchOptions>,
  ) {
    this.#set = set;
    this.#config = config;
  }

  finishSetup() {
    this.#bindPublicActions();
  }

  #bindPublicActions() {
    const { actions } = this.#config;
    const target = this as Record<string, unknown>;

    target[actions.fetch] = this.fetch.bind(this);
    target[actions.save] = this.save.bind(this);
    target[actions.delete] = this.delete.bind(this);
    target[actions.deleteAll] = this.deleteAll.bind(this);
    target[actions.reset] = this.reset.bind(this);
  }

  #listPath(options?: TFetchOptions): string {
    const { list } = this.#config.paths;
    return typeof list === 'function' ? list(options) : list;
  }

  fetch = async (options?: TFetchOptions): Promise<void | AxiosResponse<TItem[]>> => {
    this.#set({ isLoading: true, error: null } as unknown as Partial<TStore>);
    return this.internal_fetch(options);
  };

  save = async (item: TSavePayload, id?: string): Promise<void | AxiosResponse<TItem[]>> => {
    this.#set({ isLoading: true, error: null } as unknown as Partial<TStore>);
    return this.internal_save(item, id);
  };

  delete = async (id: string): Promise<void | AxiosResponse<null>> => {
    this.#set({ isLoading: true, error: null } as unknown as Partial<TStore>);
    return this.internal_delete(id);
  };

  deleteAll = async (): Promise<void | AxiosResponse<null>> => {
    this.#set({ isLoading: true, error: null } as unknown as Partial<TStore>);
    return this.internal_deleteAll();
  };

  reset = () => {
    this.internal_dispatch({ type: 'reset' });
    this.#set({ error: null, isLoading: false } as unknown as Partial<TStore>);
  };

  internal_fetch = async (options?: TFetchOptions): Promise<void | AxiosResponse<TItem[]>> => {
    const { stateKey } = this.#config;

    return runApiAction<TStore, AxiosResponse<TItem[]>>({
      set: this.#set,
      action: () => SECURE_API.get<TItem[]>(this.#listPath(options)),
      onSuccess: ({ data }) => {
        this.internal_dispatch({ type: 'setList', items: data });
        this.#set({ isLoading: false, error: null } as unknown as Partial<TStore>);
      },
      onError: error => {
        this.#set({
          [stateKey]: [],
          isLoading: false,
          error,
        } as unknown as Partial<TStore>);
      },
    });
  };

  internal_save = async (
    item: TSavePayload,
    id?: string,
  ): Promise<void | AxiosResponse<TItem[]>> => {
    const { paths } = this.#config;
    const savePromise = id
      ? SECURE_API.put<TItem[]>(paths.update(id), item)
      : SECURE_API.post<TItem[]>(paths.save, item);

    return savePromise
      .catch((error: unknown) => {
        this.#set({ error } as unknown as Partial<TStore>);
        console.log({ error });
      })
      .finally(() => {
        void this.internal_fetch();
      });
  };

  internal_delete = async (id: string): Promise<void | AxiosResponse<null>> => {
    const { paths } = this.#config;

    return SECURE_API.delete<null>(paths.delete(id))
      .catch((error: unknown) => {
        this.#set({ error } as unknown as Partial<TStore>);
        console.log({ error });
      })
      .finally(() => {
        void this.internal_fetch();
      });
  };

  internal_deleteAll = async (): Promise<void | AxiosResponse<null>> => {
    const { paths } = this.#config;

    return SECURE_API.delete<null>(paths.bulkDelete)
      .catch((error: unknown) => {
        this.#set({ error } as unknown as Partial<TStore>);
        console.log({ error });
      })
      .finally(() => {
        void this.internal_fetch();
      });
  };

  internal_dispatch = (action: ListDispatchAction<TItem>) => {
    const { stateKey } = this.#config;

    switch (action.type) {
      case 'setList':
        this.#set({ [stateKey]: action.items } as unknown as Partial<TStore>);
        break;
      case 'reset':
        this.#set({ [stateKey]: [] } as unknown as Partial<TStore>);
        break;
      default:
        break;
    }
  };
}

export const createListCrudAction = <
  TStore extends Record<string, unknown>,
  TItem extends { id: string },
  TFetchOptions = void,
  TSavePayload = TItem,
>(
  set: StoreSetter<TStore>,
  get: StoreGetter<TStore>,
  config: ListCrudConfig<TItem, TFetchOptions>,
) => {
  const impl = new ListCrudActionImpl<TStore, TItem, TFetchOptions, TSavePayload>(set, get, config);
  impl.finishSetup();
  return impl;
};
